import { CommonModule, DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Renderer2,
  Inject,
  inject,
} from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TypeSectionNavigationFields } from "../../../models/contentful-content-types/section-navigation";
import { Router, RouterModule, UrlSegment, UrlSegmentGroup, UrlTree } from "@angular/router";
import { ScrollAnchorDirective } from "../../utils/directives/scroll-anchor.directive";

type NavLink = {
  fields: { name?: string; label?: string; url?: string; isAnchor?: boolean };
};

@Component({
  selector: "jnguyen-section-navigation",
  imports: [CommonModule, FontAwesomeModule, RouterModule, ScrollAnchorDirective],
  templateUrl: "./section-navigation.component.html",
  styleUrls: ["./section-navigation.component.scss"],
})
export class SectionNavigationComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() data!: TypeSectionNavigationFields;

  activeId: string | null = null;

  // ✅ einziges Mobile-State-Flag
  mobileMenuIsVisible = false;
  desktopMenuIsVisible = false;

  private observer?: IntersectionObserver;
  private viewReady = false;

  get links(): NavLink[] {
    return (this.data?.links as unknown as NavLink[]) ?? [];
  }
  private readonly router = inject(Router);

  constructor(
    @Inject(DOCUMENT) readonly document: Document,

    readonly renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.setupObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (changes["data"]) this.setupObserver();
  }

  private setupObserver(): void {
    // SSR guard
    if (typeof document === "undefined") return;

    this.observer?.disconnect();
    this.observer = undefined;

    const sections = this.links
      .map((i) => {
        const name = i?.fields?.name ?? "";
        return name ? this.document.getElementById(name) : null;
      })
      .filter((el): el is HTMLElement => !!el);

    if (!sections.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.id) this.activeId = visible.target.id;
      },
      {
        root: null,
        threshold: [0.1, 0.25, 0.5, 0.75, 1],
        rootMargin: "-80px 0px -40% 0px",
      },
    );

    sections.forEach((el) => this.observer?.observe(el));
  }

  // ✅ Mobile Menu Handling (wie alt)
  toggleMobileMenu(): void {
    if (this.mobileMenuIsVisible) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu(): void {
    this.mobileMenuIsVisible = true;
    this.updateBodyLock();
  }

  closeMobileMenu(): void {
    this.mobileMenuIsVisible = false;
    this.updateBodyLock();
  }

  // ✅ wie altes navigateTo(): schließt menu
  onMobileNavigate(): void {
    this.closeMobileMenu();
  }

  toggleDesktopMenu(): void {
    if (this.desktopMenuIsVisible) {
      this.closeDesktopMenu();
    } else {
      this.openDesktopMenu();
    }
  }

  openDesktopMenu(): void {
    this.desktopMenuIsVisible = true;
    this.updateBodyLock();
  }

  closeDesktopMenu(): void {
    this.desktopMenuIsVisible = false;
    this.updateBodyLock();
  }

  onDesktopNavigate(): void {
    this.closeDesktopMenu();
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.mobileMenuIsVisible) this.closeMobileMenu();
    if (this.desktopMenuIsVisible) this.closeDesktopMenu();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();

    // Safety: Body class entfernen
    this.mobileMenuIsVisible = false;
    this.desktopMenuIsVisible = false;
    this.updateBodyLock();
  }

  private updateBodyLock(): void {
    if (this.mobileMenuIsVisible || this.desktopMenuIsVisible) {
      this.renderer.addClass(this.document.body, "modal-open");
      return;
    }
    this.renderer.removeClass(this.document.body, "modal-open");
  }
  private buildPopupUrlTree(segments: string[]): UrlTree {
    const tree = this.router.parseUrl(this.router.url);
    tree.root.children["popup"] = new UrlSegmentGroup(
      segments.map((s) => new UrlSegment(s, {})),
      {},
    );
    return tree;
  }

  private parsePopup(url: string | undefined): string[] | null {
    if (!url) return null;
    const re = /^popup:(.+)$/i;
    const m = re.exec(url);
    if (!m) return null;
    const segs = m[1]
      .split("/")
      .map((s) => s.trim())
      .filter(Boolean);
    return segs.length ? segs : null;
  }

  isExternalLink(url: string | undefined): boolean {
    if (!url) return false;
    return /^(https?:|mailto:|tel:|\/\/)/i.test(url);
  }

  isPopupLink(url: string | undefined): boolean {
    return !!this.parsePopup(url);
  }

  isHashLink(url: string | undefined): boolean {
    if (!url) return false;
    return /^#/i.test(url);
  }

  routerLinkTarget(url: string | undefined): string | UrlTree | null {
    if (!url) return null;
    const popupSegs = this.parsePopup(url);
    if (popupSegs) return this.buildPopupUrlTree(popupSegs);
    return url;
  }
}
